import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Checkbox, Collapse, FormControlLabel, Typography, RadioGroup, Radio, FormLabel,
} from '@mui/material';
import {
  CardWrapper, SectionTitle, Field, PeriodoField,
} from '../UI';
import { PESQUISAS_PRONTAS } from '../../constants/pesquisas.constants';
import * as C from '../../theme/colors.constants';

/**
 * Pesquisas prontas.
 *
 * No abe-web-react isso já existe como "Pesquisas Especiais"
 * (`PesquisaEspecialProcesso.jsx`): checkbox + campos condicionais que só
 * aparecem quando o item é marcado. Mantive esse comportamento e os mesmos
 * rótulos, apenas atualizando a lista para a do pedido.
 *
 * "Processos Expirados ou a Expirar" ganhou o par de rádios que existe no
 * código real (Expirados / Próximos a Expirar) além do Cód. Cliente pedido.
 */
export default function PesquisasProntas({
  valores, onChange, dropdownItems, pesquisaAtiva, onChangePesquisaAtiva,
}) {
  const toggle = id => () => onChangePesquisaAtiva(pesquisaAtiva === id ? null : id);
  const setParam = (pesquisaId, paramId) => (valorOuEvento) => {
    const valor = valorOuEvento?.target ? valorOuEvento.target.value : valorOuEvento;
    if (onChange) onChange({ ...valores, [`${pesquisaId}.${paramId}`]: valor });
  };

  const renderParam = (pesquisa, param) => {
    const key = `${pesquisa.id}.${param.id}`;
    if (param.tipo === 'periodo') {
      return (
        <PeriodoField
          key={key}
          label={`${param.label} (inicial e final)`}
          valor={valores[key] || {}}
          onChange={setParam(pesquisa.id, param.id)}
          separador="até"
        />
      );
    }
    const options = param.selectorKey ? (dropdownItems[param.selectorKey] || []) : param.options;
    return (
      <Field
        key={key}
        label={param.label}
        tipo={param.tipo}
        options={options}
        value={valores[key] || ''}
        onChange={setParam(pesquisa.id, param.id)}
      />
    );
  };

  return (
    <CardWrapper>
      <SectionTitle hint="Filtros pré-configurados. Marque um item para informar os parâmetros.">
        Pesquisas prontas
      </SectionTitle>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
          columnGap: 'clamp(16px, 1.6vw, 44px)',
          rowGap: 0.5,
        }}
      >
        {PESQUISAS_PRONTAS.map((p) => {
          const ativo = pesquisaAtiva === p.id;
          const desabilitado = pesquisaAtiva !== null && !ativo;
          const temParams = p.params.length > 0;
          return (
            <Box
              key={p.id}
              sx={{
                py: 0.5,
                borderBottom: `1px solid ${ativo ? 'transparent' : C.borderGrey}`,
                backgroundColor: ativo ? C.paleGrey : 'transparent',
                borderRadius: ativo ? 2 : 0,
                px: ativo ? 1.5 : 0,
                transition: 'background-color .15s ease',
              }}
            >
              <FormControlLabel
                disabled={desabilitado}
                control={<Checkbox checked={ativo} onChange={toggle(p.id)} size="small" />}
                label={(
                  <Typography variant="body2" sx={{ fontWeight: ativo ? 700 : 400, color: C.black }}>
                    {p.label}
                    {temParams ? (
                      <Typography component="span" variant="caption" sx={{ color: C.warmGrey, ml: 0.75 }}>
                        (
                        {p.params.map(x => x.label).join(', ')}
                        )
                      </Typography>
                    ) : null}
                  </Typography>
                )}
              />

              <Collapse in={ativo && temParams} unmountOnExit>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: p.params.length > 1 ? 'minmax(0, 1fr) minmax(0, 1fr)' : 'minmax(0, 1fr)' },
                    gap: 1.5,
                    pb: 1.5,
                    pl: 3.75,
                  }}
                >
                  {p.id === 'expirados' ? (
                    <Box>
                      <FormLabel sx={{ fontSize: 12, fontWeight: 700, color: C.black }}>Situação</FormLabel>
                      <RadioGroup
                        row
                        value={valores[`${p.id}.situacao`] || 'Expirados'}
                        onChange={setParam(p.id, 'situacao')}
                      >
                        <FormControlLabel value="Expirados" control={<Radio size="small" />} label={<Typography variant="body2">Expirados</Typography>} />
                        <FormControlLabel value="Próximos a Expirar" control={<Radio size="small" />} label={<Typography variant="body2">Próximos a Expirar</Typography>} />
                      </RadioGroup>
                    </Box>
                  ) : null}
                  {p.params.map(param => renderParam(p, param))}
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </CardWrapper>
  );
}
PesquisasProntas.propTypes = {
  valores: PropTypes.object.isRequired,
  onChange: PropTypes.func,
  dropdownItems: PropTypes.object,
  pesquisaAtiva: PropTypes.string,
  onChangePesquisaAtiva: PropTypes.func.isRequired,
};
PesquisasProntas.defaultProps = {
  onChange: null,
  dropdownItems: {},
  pesquisaAtiva: null,
};
